import {
  theme as defaultTheme,
  Icon,
  SearchField,
  Button,
} from "@mindlercare/ui-web";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { styled } from "styled-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CONSTANTS from "core/constants";
import { format, fromUnixTime } from "date-fns";
import { Typography } from "components/typography";
import { Filter } from "./types";

interface FilterBarProps {
  onChangeFilter: (id: string, value: string) => void;
  onApplyFilters: () => void;
  filters: Filter[];
}

export const Filters = ({
  filters: fs,
  onChangeFilter,
  onApplyFilters,
}: FilterBarProps) => {
  const dateFilters = fs.filter((filter) => filter.type === "date");
  const textFilters = fs.filter((filter) => filter.type === "text");

  const [openIdx, setOpenIdx] = useState(-1);
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDateChange = (id: string) => (e: any) => {
    onChangeFilter(id, e);
  };

  const onSearchFieldChange = (id: string) => (value: string) => {
    onChangeFilter(id, value);
  };

  return (
    <div>
      <StyledContainer>
        <StyledCalendarFields>
          {dateFilters.map((filter, index) => {
            const date = format(
              fromUnixTime(filter.value as number),
              CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
            );

            return (
              <div key={`filter-datepicker-${filter.id}-${index}`}>
                <Typography
                  insensitive
                  variant="body-x-small"
                  color="GRAY_DARK"
                >
                  {filter.label}
                </Typography>
                <StyledCalendarField data-testid={"date-picker"}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      open={openIdx === index}
                      onClose={() => setOpenIdx(-1)}
                      value={new Date(date)}
                      onChange={onDateChange(filter.id)}
                      onAccept={onDateChange(filter.id)}
                      format="yyyy-MM-dd"
                      slotProps={{
                        textField: {
                          InputProps: {
                            endAdornment: null,
                            disableUnderline: true,
                          },
                          variant: "standard",
                          onClick: () => setOpenIdx(index),
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <Icon.Calendar />
                </StyledCalendarField>
              </div>
            );
          })}
        </StyledCalendarFields>

        {textFilters.length > 0 && (
          <StyledTextFields>
            {textFilters.map((filter, index) => {
              return (
                <SearchField
                  sx={{ width: "268px" }}
                  key={`filter-text-${filter.id}-${index}`}
                  placeholder={filter.label}
                  onChange={onSearchFieldChange(filter.id)}
                />
              );
            })}
          </StyledTextFields>
        )}
      </StyledContainer>
      <ButtonsContainer>
        <FilterButton variant="primary" onClick={onApplyFilters}>
          {t("SEARCH")}
        </FilterButton>
      </ButtonsContainer>
    </div>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledCalendarFields = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledCalendarField = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(p) => defaultTheme.COLORS.GRAY_MEDIUM};
  width: 250px;
  height: 48px;
  padding: 12px 16px;
  margin: 0 1.5rem 0 0;
  border-radius: 4px;
`;

const StyledTextFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 1.5rem;
  row-gap: 1.5rem;
  margin-top: 1.5rem;
`;

const FilterButton = styled(Button)`
  margin-bottom: 4rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  column-gap: 1rem;
  margin-top: 1.5rem;
`;
